import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Text,
  Divider,
  Button,
  Hide,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { NestedHome, NestedUser } from '@habity/types';
import {
  MdAdminPanelSettings,
  MdCode,
  MdGridView,
  MdHelpOutline,
  MdImage,
  MdPeople,
  MdSync,
  MdVisibility,
  MdVisibilityOff,
} from 'react-icons/md';
import { capitalize } from 'lodash';
import { RemoveItemPopover } from '~/components/ui/popovers/RemoveItem';
import { TbTrash } from 'react-icons/tb';
import { isHomeOwner, getSpeculatorsPath } from '@habity/services';
import { isAdmin, isBrokerOrAdmin } from '~/lib/roles';
import { useUIStore } from '~/store';
import { useVitec } from '~/hooks/providers/useVitec';
import { useHomes } from '~/hooks/useHomes';

interface AdminMenuProps {
  home: NestedHome;
  user: NestedUser | null;
  cmsUrl?: string;
  gridLayoutPreference: number;
  setGridLayoutPreference: (n: number) => void;
}

export const AdminMenu = ({
  home,
  user,
  gridLayoutPreference,
  setGridLayoutPreference,
}: AdminMenuProps) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const { updateHome, deleteHome } = useHomes();
  const { syncEstate } = useVitec();
  const { provider, provider_id } = home || {};
  const modals = useUIStore((state) => state.modals);
  const setModals = useUIStore((state) => state.setModals);

  // Only exists if the user is an admin or broker
  if (!user || !isBrokerOrAdmin(user)) {
    return null;
  }

  function onViewSpeculators() {
    router.push(getSpeculatorsPath(home));
  }

  return (
    <Menu isLazy>
      <MenuButton
        // leftIcon={<MdAdminPanelSettings />}
        as={Button}
        variant="secondary"
        aria-label="Admin options"
        size="sm"
      >
        <HStack gap="1">
          <Icon as={MdAdminPanelSettings} />
          <Hide below="desktop">
            <Text fontWeight="medium" size="sm">
              {isAdmin(user) ? 'Administratör' : 'Mäklarverktyg'}
            </Text>
          </Hide>
        </HStack>
      </MenuButton>
      <MenuList zIndex={100}>
        <Text fontWeight="medium" px={3} py={2} fontSize="sm" color="gray.600">
          {isAdmin(user)
            ? 'Inloggad som administratör'
            : 'Inloggad som mäklare'}
        </Text>
        <Divider mb={2} />

        <MenuItem icon={<MdPeople />} onClick={onViewSpeculators}>
          Matcha spekulanter
        </MenuItem>

        {!isAdmin(user) && (
          <MenuItem
            icon={<MdHelpOutline />}
            as="a"
            href="https://support.erikolsson.se"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mäklare FAQ
          </MenuItem>
        )}

        {/* Grid layout preference */}
        {/* Hide for brokers until "released" */}
        {isAdmin(user) && (
          <MenuItem
            icon={<MdGridView />}
            onClick={() => {
              if (gridLayoutPreference === 5) return setGridLayoutPreference(6);
              return setGridLayoutPreference(5);
            }}
          >
            Använd {gridLayoutPreference === 5 ? 6 : 5}-bildslayout
          </MenuItem>
        )}

        {/* Sync */}
        {provider_id && (
          <MenuItem
            icon={isLoading ? <Spinner size="sm" /> : <MdSync />}
            onClick={async () => {
              setIsLoading(true);
              try {
                await syncEstate(home);
                router?.refresh();
              } catch (error) {
                console.error('Failed to sync home:', error);
              } finally {
                setIsLoading(false);
              }
            }}
          >
            Synka med {provider ? capitalize(provider) : 'källan'}
          </MenuItem>
        )}

        {/* View source data */}
        {provider_id && (
          <MenuItem
            icon={<MdCode />}
            onClick={() =>
              setModals({
                ...modals,
                providerDataOpen: true,
              })
            }
          >
            Visa källdata
          </MenuItem>
        )}

        {/* Image Ads */}
        {isAdmin(user) && (
          <MenuItem
            icon={<MdImage />}
            onClick={() => router.push('/dashboard/admin/ads')}
          >
            Bildannonser
          </MenuItem>
        )}

        {/* Toggle visibility */}
        {isAdmin(user) && (
          <MenuItem
            isDisabled
            icon={
              isUpdatingVisibility ? (
                <Spinner size="sm" />
              ) : home.is_hidden ? (
                <MdVisibility />
              ) : (
                <MdVisibilityOff />
              )
            }
            onClick={async () => {
              setIsUpdatingVisibility(true);
              try {
                await updateHome(home.id, {
                  is_hidden: !home.is_hidden,
                });
                router?.refresh();
              } catch (error) {
                console.error('Failed to update home visibility:', error);
              } finally {
                setIsUpdatingVisibility(false);
              }
            }}
          >
            {home.is_hidden ? 'Visa bostaden publikt' : 'Dölj bostaden'}
          </MenuItem>
        )}

        {/* Delete */}
        {(isAdmin(user) || isHomeOwner(home, user)) && (
          <>
            <Divider my={2} />
            <RemoveItemPopover
              tooltip="Radera denna bostad och ta bort profilen permanent."
              triggerButton={
                <MenuItem
                  isDisabled
                  icon={<TbTrash />}
                  color="red.500"
                  as="button"
                  width="100%"
                  textAlign="left"
                >
                  Radera bostad
                </MenuItem>
              }
              title={`Är du säker på att du vill radera ${
                home.address?.street || 'denna bostad'
              }? Detta kommer att ta bort bostaden permanent och kan inte ångras.`}
              onConfirm={async () => {
                try {
                  await deleteHome(home.id);
                  router.push('/');
                } catch (error) {
                  console.error('Failed to delete home:', error);
                }
              }}
            />
          </>
        )}
      </MenuList>
    </Menu>
  );
};
